import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { BlogPost } from '@/components/BlogCard';
import { ArrowLeft, BookmarkCheck, Trash2, BookOpen, Building2, PlayCircle, PenLine } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const SAVED_ARTICLES_KEY = 'kaufpause-saved-articles';

const SavedArticles = () => {
  const navigate = useNavigate();
  const [savedArticles, setSavedArticles] = useState<BlogPost[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadSavedArticles();
  }, []);

  const loadSavedArticles = () => {
    setIsLoading(true);
    try {
      const saved = localStorage.getItem(SAVED_ARTICLES_KEY);
      if (saved) {
        const articles = JSON.parse(saved);
        setSavedArticles(articles);
      } else {
        setSavedArticles([]);
      }
    } catch (error) {
      console.error('Fehler beim Laden der gespeicherten Artikel:', error);
      toast.error('Die gespeicherten Artikel konnten nicht geladen werden');
      setSavedArticles([]);
    } finally {
      setIsLoading(false);
    }
  };

  const removeArticle = (slug: string) => {
    try {
      const updatedArticles = savedArticles.filter(article => article.slug !== slug);
      localStorage.setItem(SAVED_ARTICLES_KEY, JSON.stringify(updatedArticles));
      setSavedArticles(updatedArticles);
      toast.success('Artikel aus Lesezeichen entfernt');
    } catch (error) {
      console.error('Fehler beim Entfernen des Artikels:', error);
      toast.error('Der Artikel konnte nicht entfernt werden');
    }
  };

  const clearAllArticles = () => {
    try {
      localStorage.removeItem(SAVED_ARTICLES_KEY);
      setSavedArticles([]);
      toast.success('Alle gespeicherten Artikel wurden entfernt');
    } catch (error) {
      console.error('Fehler beim Löschen aller Artikel:', error);
      toast.error('Die gespeicherten Artikel konnten nicht gelöscht werden');
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'dd. MMMM yyyy', { locale: de });
    } catch {
      return dateString;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50/50">
      {/* Navigation - identisch zu anderen Seiten */}
      <nav className="fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-md border-b border-gray-100 z-50">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <a 
              href="/" 
              className="flex items-center gap-2 hover:scale-105 transition-all duration-300 cursor-pointer group"
            >
              <img 
                src="/Favicon/favicon-196x196.png" 
                alt="KaufPause Logo" 
                className="w-8 h-8 -translate-y-0.5"
              />
              <span className="text-xl font-bold text-gray-900">
                Kauf<span className="bg-gradient-to-r from-emerald-600 to-emerald-700 bg-clip-text text-transparent">Pause</span>
              </span>
            </a>
          </div>
          
          <div className="hidden md:flex items-center justify-center gap-8 absolute left-1/2 transform -translate-x-1/2">
            <button
              onClick={() => navigate('/blog')}
              className="text-emerald-600 bg-emerald-50 transition-colors duration-200 font-medium px-3 py-2 rounded-lg flex items-center gap-2"
            >
              <BookOpen className="w-4 h-4" />
              Blog
            </button>
            <button
              onClick={() => navigate('/institutionen')}
              className="text-gray-600 hover:text-emerald-600 transition-colors duration-200 font-medium hover:bg-emerald-50 px-3 py-2 rounded-lg flex items-center gap-2"
            >
              <Building2 className="w-4 h-4" />
              Für Institutionen
            </button>
            <button
              onClick={() => navigate('/demo')}
              className="text-gray-600 hover:text-emerald-600 transition-colors duration-200 font-medium hover:bg-emerald-50 px-3 py-2 rounded-lg flex items-center gap-2"
            >
              <PlayCircle className="w-4 h-4" />
              Demo testen
            </button>
          </div>

          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => signOut()}
              className="text-gray-600 hover:text-emerald-600"
            >
              Abmelden
            </Button>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 pt-20 pb-16 max-w-4xl">
        {/* Back button */}
        <Button 
          variant="ghost" 
          className="mb-6 flex items-center gap-2 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 rounded-xl"
          onClick={() => navigate('/blog')}
        >
          <ArrowLeft className="h-4 w-4" />
          Zurück zur Übersicht
        </Button>

        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-emerald-600 via-emerald-700 to-green-600 bg-clip-text text-transparent mb-2 flex items-center gap-2">
              <BookmarkCheck className="h-6 w-6 text-emerald-600" />
              Gespeicherte Artikel
            </h1>
            <p className="text-gray-600">
              Hier findest du alle Artikel, die du für später gespeichert hast
            </p>
          </div>

          {savedArticles.length > 0 && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50 rounded-xl">
                  <Trash2 className="h-4 w-4 mr-2" />
                  Alle löschen
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="rounded-2xl">
                <AlertDialogHeader>
                  <AlertDialogTitle>Alle gespeicherten Artikel löschen?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Bist du sicher, dass du alle gespeicherten Artikel löschen möchtest? Diese Aktion kann nicht rückgängig gemacht werden.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="rounded-xl">Abbrechen</AlertDialogCancel>
                  <AlertDialogAction onClick={clearAllArticles} className="bg-red-600 hover:bg-red-700 rounded-xl">
                    Alle löschen
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>

        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-emerald-500"></div>
          </div>
        ) : savedArticles.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-xl p-8 text-center">
            <BookmarkCheck className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-medium text-gray-900 mb-2">Keine gespeicherten Artikel</h3>
            <p className="text-gray-600 mb-6">
              Du hast noch keine Artikel gespeichert. Klicke auf den "Speichern"-Button auf einem Artikel, 
              um ihn hier anzuzeigen.
            </p>
            <Button onClick={() => navigate('/blog')} className="rounded-xl">
              Zur Blogübersicht
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {savedArticles.map((article, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl border border-gray-100 shadow-xl overflow-hidden flex flex-col md:flex-row hover:shadow-2xl transition-shadow duration-300"
              >
                <div 
                  className="w-full md:w-1/4 h-40 md:h-auto bg-cover bg-center cursor-pointer" 
                  style={{ backgroundImage: `url(${article.imageUrl || 'https://images.unsplash.com/photo-1591696205602-2f950c417cb9?q=80&w=800&auto=format&fit=crop'})` }}
                  onClick={() => navigate(`/blog/${article.slug}`)}
                />
                <div className="p-4 md:p-6 flex-1 flex flex-col justify-between">
                  <div>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {article.categories && article.categories.map((category, i) => (
                        <span key={i} className="px-2 py-1 bg-emerald-50 text-emerald-700 text-xs rounded-xl">
                          {category}
                        </span>
                      ))}
                    </div>
                    <h2 
                      className="text-xl font-bold text-gray-900 mb-2 hover:text-emerald-600 cursor-pointer transition-colors duration-200"
                      onClick={() => navigate(`/blog/${article.slug}`)}
                    >
                      {article.title}
                    </h2>
                    <p className="text-gray-600 text-sm line-clamp-2 mb-4">
                      {article.excerpt}
                    </p>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500 text-sm">
                      {article.date ? formatDate(article.date) : 'Kein Datum'}
                    </span>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-red-500 hover:text-red-700 hover:bg-red-50 rounded-xl"
                      onClick={() => removeArticle(article.slug)}
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Entfernen
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SavedArticles; 